import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to EcomKit");
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr,1.1fr] bg-background">
      <div className="flex items-center justify-center px-6 py-16 lg:py-20 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <div className="logo-mark">EK</div>
            <span className="font-display font-semibold text-navy">EcomKit</span>
          </Link>
          <h1 className="font-display font-bold text-4xl tracking-tighter text-navy">Start free</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-8">1 marketplace free. No credit card required.</p>

          <form onSubmit={submit} className="space-y-4">
            {[
              ["name", "Full name", "text"],
              ["email", "Email", "email"],
              ["password", "Password", "password"],
              ["org_name", "Store name (optional)", "text"],
            ].map(([k, label, type]) => (
              <div key={k}>
                <label className="eyebrow block mb-1.5">{label}</label>
                <input data-testid={`signup-${k}`} type={type} required={k !== "org_name"}
                  value={form[k]} onChange={(e)=>set(k, e.target.value)} className="input-base"
                  placeholder={k === "org_name" ? "Acme Traders" : ""}/>
              </div>
            ))}
            <button data-testid="signup-submit" disabled={loading} type="submit" className="btn-primary w-full justify-center py-3">
              {loading ? "Creating…" : <>Create account <ArrowRight size={14} weight="bold"/></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-orange font-semibold">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-navy text-white order-1 lg:order-2 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, hsl(var(--ecom-orange)) 0%, transparent 40%), radial-gradient(circle at 80% 80%, hsl(var(--ecom-green)) 0%, transparent 45%)"
        }}/>
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="eyebrow text-white/60">EcomKit · Command Center</div>
          <div>
            <h2 className="font-display font-bold text-5xl leading-[1.05] tracking-tighter">
              Set up your<br/>catalog in minutes.
            </h2>
            <p className="mt-4 text-white/70 text-lg max-w-md">
              Connect your first marketplace and we'll import your products, detect gaps and score every listing.
            </p>
          </div>
          <div className="flex items-center gap-6 text-xs font-mono-data text-white/50">
            <span>10 seeded products</span>
            <span>1-click AI</span>
            <span>Instant health score</span>
          </div>
        </div>
      </div>
    </div>
  );
}
